import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RevenueSource, getRevenueSourceDisplayName, getAvailableRevenueSources } from '../../../types/RevenueSource';
import { RevenueSourceType, getRevenueSourceTypeDisplayName, getAvailableRevenueSourceTypes } from '../../../types/RevenueSourceType';
import { Plus, X } from 'lucide-react';

interface Props {
  revenueSourceName: RevenueSource | null;
  revenueSourceType: RevenueSourceType | null;
  restaurantNameInPOS: string;
  relevantSections: string[];
  onChangeSourceName: (name: RevenueSource) => void;
  onChangeSourceType: (type: RevenueSourceType) => void;
  onChangeRestaurantName: (name: string) => void;
  onChangeSections: (sections: string[]) => void;
  onNext: () => void;
}

const ConfigureRevenueStep: React.FC<Props> = ({
  revenueSourceName,
  revenueSourceType,
  restaurantNameInPOS,
  relevantSections,
  onChangeSourceName,
  onChangeSourceType,
  onChangeRestaurantName,
  onChangeSections,
  onNext,
}) => {
  const { t } = useTranslation();
  const [sectionInput, setSectionInput] = useState('');

  const addSection = () => {
    const trimmed = sectionInput.trim();
    if (trimmed && !relevantSections.includes(trimmed)) {
      onChangeSections([...relevantSections, trimmed]);
      setSectionInput('');
    }
  };

  const removeSection = (section: string) => {
    onChangeSections(relevantSections.filter((s) => s !== section));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSection();
    }
  };

  const canProceed = revenueSourceName && revenueSourceType && restaurantNameInPOS.trim();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text dark:text-text-dark">{t('wizard.configureRevenue.title')}</h2>
        <p className="text-muted dark:text-muted-dark mt-2">{t('wizard.configureRevenue.subtitle')}</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text dark:text-text-dark mb-1">
            {t('wizard.configureRevenue.sourceLabel')}
          </label>
          <select
            value={revenueSourceName || ''}
            onChange={(e) => onChangeSourceName(e.target.value as RevenueSource)}
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-background dark:bg-background-dark text-text dark:text-text-dark"
          >
            <option value="">{t('wizard.configureRevenue.selectSource')}</option>
            {getAvailableRevenueSources().map((src) => (
              <option key={src} value={src}>
                {getRevenueSourceDisplayName(src)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text dark:text-text-dark mb-1">
            {t('wizard.configureRevenue.typeLabel')}
          </label>
          <select
            value={revenueSourceType || ''}
            onChange={(e) => onChangeSourceType(e.target.value as RevenueSourceType)}
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-background dark:bg-background-dark text-text dark:text-text-dark"
          >
            <option value="">{t('wizard.configureRevenue.selectType')}</option>
            {getAvailableRevenueSourceTypes().map((type) => (
              <option key={type} value={type}>
                {getRevenueSourceTypeDisplayName(type)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text dark:text-text-dark mb-1">
            {t('wizard.configureRevenue.restaurantNameLabel')}
          </label>
          <p className="text-xs text-muted dark:text-muted-dark mb-2">
            {t('wizard.configureRevenue.restaurantNameHint')}
          </p>
          <input
            type="text"
            value={restaurantNameInPOS}
            onChange={(e) => onChangeRestaurantName(e.target.value)}
            placeholder={t('wizard.configureRevenue.restaurantNamePlaceholder')}
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-background dark:bg-background-dark text-text dark:text-text-dark"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text dark:text-text-dark mb-1">
            {t('wizard.configureRevenue.sectionsLabel')}
          </label>
          <p className="text-xs text-muted dark:text-muted-dark mb-2">
            {t('wizard.configureRevenue.sectionsHint')}
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={sectionInput}
              onChange={(e) => setSectionInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('wizard.configureRevenue.sectionPlaceholder')}
              className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-background dark:bg-background-dark text-text dark:text-text-dark"
            />
            <button
              onClick={addSection}
              disabled={!sectionInput.trim()}
              className="px-3 py-2 rounded-lg bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {relevantSections.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {relevantSections.map((section) => (
                <span
                  key={section}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-zinc-100 dark:bg-zinc-700 text-sm text-text dark:text-text-dark"
                >
                  {section}
                  <button onClick={() => removeSection(section)} className="hover:text-red-500 dark:hover:text-red-400 transition">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="px-6 py-3 rounded-xl bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition font-medium disabled:opacity-50"
        >
          {t('wizard.next')}
        </button>
      </div>
    </div>
  );
};

export default ConfigureRevenueStep;
